import React, { useRef } from "react"
import PropTypes from "prop-types"
import { NavLink, useMatch, Link } from "react-router-dom"
import { routes } from "router/routes"
import UserMenu from "components/common/UserMenu"
import GuideNavigation from "./GuideNavigation"
import TripFiltersNavigation from "./TripFiltersNavigation"
import SearchTripsAntd from "./SearchTripsAntd"
import withDefaultErrorBoundary from "modules/errors/HOCs/withDefaultErrorBoundary"
import Logo from "components/common/Logo"
import Avatar from "components/common/Avatar"
import useEvent from "hooks/useEvent"
import { useDispatch, useSelector } from "react-redux"
import { updateWindowSize } from "modules/window/reducer"
import { isGuideSelector, updateUser } from "store/user"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useTranslation } from "react-i18next"
import { isProApp } from "utils/app-type"
import { getHostsByEnvironment } from "utils/host-urls"
import { Button, Flex, Switch, Layout, Typography, Space } from "antd"
import styles from "./style.module.css"

const { Header: AntdHeader, Content } = Layout

GuestHeader.propTypes = {
  noPadding: PropTypes.bool,
  fluid: PropTypes.bool,
  withNavigation: PropTypes.bool,
  withBotomBorder: PropTypes.bool,
  className: PropTypes.string
}

function GuestHeader({ noPadding = false, fluid = false, withNavigation = false, withBotomBorder: bottomBorder = false, className }) {
  const dispatch = useDispatch()
  const headerRef = useRef(null)
  const headerHeight = useSelector((store) => store.window)
  const { switchTheme, isSportsman } = useTheme()
  const isGuide = useSelector(isGuideSelector)
  const user = useSelector((store) => store.user)
  const { loggedIn } = useSelector((store) => store.auth)
  const { t } = useTranslation()

  const isProHost = isProApp()
  const { PROTOCOL, PRO_LANDING, CUSTOMER } = getHostsByEnvironment()
  const isClientLandingPage = !!useMatch(routes.rootPath()) && !isProHost
  const isGuideProfile = !!useMatch(routes.proGuideProfilePath())

  const isTripsPage = !!useMatch(routes.tripsPath())

  // Logo redirect logic based on user type
  let logoPath
  let logoIsExternal = false

  if (loggedIn && isGuide && isGuideProfile) {
    // Guide logged in viewing guide profile → guide dashboard
    logoPath = routes.guideRootPath()
  } else if (loggedIn && !isGuide) {
    // Guest logged in → marketplace /trips
    if (isProHost && isGuideProfile) {
      // On pro host, redirect to client host (include # for hash routing)
      logoPath = `${PROTOCOL}${CUSTOMER}/#${routes.tripsPath()}`
      logoIsExternal = true
    } else {
      logoPath = routes.tripsPath()
    }
  } else if (!loggedIn) {
    // Not logged in → marketplace root page (/)
    if (isProHost && isGuideProfile) {
      // On pro host, redirect to client host (include # for hash routing)
      logoPath = `${PROTOCOL}${CUSTOMER}/#${routes.rootPath()}`
      logoIsExternal = true
    } else {
      logoPath = routes.rootPath()
    }
  } else {
    // Fallback
    logoPath = routes.rootPath()
  }

  // Login button always redirects to guest login (client host when on pro host)
  // Include # for hash routing when redirecting to external host
  const signInPath = isProHost ? `${PROTOCOL}${CUSTOMER}/#${routes.signInV2()}` : routes.signInV2()

  const showGuideNavigation = withNavigation && isGuide
  const showTripFiltersNavigation = (isTripsPage && !isGuide) || isClientLandingPage
  const withBotomBorder = bottomBorder || showGuideNavigation || showTripFiltersNavigation

  const setHeight = () => {
    if (!headerRef.current) return
    const height = headerRef.current.clientHeight
    const isHeightChanged = headerHeight !== height
    if (!isHeightChanged) return
    document.body.style.setProperty("--header-height", `${height}px`)
    dispatch(updateWindowSize({ headerHeight: height }))
  }

  useEvent("load", setHeight, true, window, true, true)
  useEvent("resize", setHeight, true, window)

  const classes = [styles.headerRoot, "sticky-top"]
  if (withBotomBorder) classes.push(styles.withBotomBorder)
  if (className) classes.push(className)

  const handleThemeChange = (checked) => {
    switchTheme(isSportsman ? themes.sportswoman : themes.sportsman)
    dispatch(updateUser({ is_sportsman: !isSportsman }))
  }

  const scrollToRequestDemo = () => {
    const demoRequestEl = document.querySelector("#demo-request")
    if (demoRequestEl) demoRequestEl.scrollIntoView({ behavior: "smooth" })
  }

  return (
    <div
      className={styles.stickyTopHeader}
      ref={headerRef}
      style={{ ...(isTripsPage && { maxHeight: 140 }), position: "sticky", top: 0, zIndex: 1000 }}
    >
      <AntdHeader className={`${classes.join(" ")}, layout-wrapper`} style={{ background: "#fff" }}>
        <Content style={{ width: "100%" }}>
          <Flex gap={10} justify="space-between" wrap="wrap" align="center">
            <Space direction="horizontal" size={32} align="center" className={styles.logoAndButtons}>
              {logoIsExternal ? (
                <a href={logoPath} title="Home" className={styles.logoWrapper}>
                  {isSportsman ? (
                    <Logo alt="Logo" width={35} height={35} wide />
                  ) : (
                    <Logo alt="Logo" width={35} height={35} is_sportswoman wide />
                  )}
                </a>
              ) : (
                <NavLink to={logoPath} state={{ scrollToTop: true }} title="Home" className={styles.logoWrapper}>
                  {isSportsman ? (
                    <Logo alt="Logo" width={35} height={35} wide />
                  ) : (
                    <Logo alt="Logo" width={35} height={35} is_sportswoman wide />
                  )}
                </NavLink>
              )}
              {!loggedIn && (
                <>
                  {!isProHost && (
                    <a href={PROTOCOL + PRO_LANDING} target="_blank" rel="noopener noreferrer">
                      <Button className={styles.headerBtn} type="primary">
                        {t("global.become_a_guide")}
                      </Button>
                    </a>
                  )}
                  {isProHost && !isGuideProfile && (
                    <Button className={styles.headerBtn} type="link" style={{ fontFamily: "Epilogue" }} onClick={scrollToRequestDemo}>
                      See a Demo
                    </Button>
                  )}
                </>
              )}
            </Space>
            <div className={styles.searchTripsWrap}>
              {/* Show search on guest pages and guide profile pages */}
              {(!isProHost || isGuideProfile) && (
                <SearchTripsAntd
                  className={styles.searchTrips}
                  disabled={isGuideProfile && ((!loggedIn && isProHost) || (loggedIn && isGuide))}
                />
              )}
            </div>
            {!loggedIn &&
              (isProHost ? (
                <a href={signInPath} className={styles.authButtons}>
                  <Button className={`${styles.headerBtn} px-m py-s`} type="primary">
                    {t("global.sign_in")}
                  </Button>
                </a>
              ) : (
                <Link to={signInPath} className={styles.authButtons}>
                  <Button className={`${styles.headerBtn} px-m py-s`} type="primary">
                    {t("global.sign_in")}
                  </Button>
                </Link>
              ))}
            {loggedIn && (
              <div className="my-2">
                <Flex align="center" gap={10}>
                  <Switch checked={isSportsman} onChange={handleThemeChange} className={styles.themeToggle} />
                  {/* Guide viewing guide profile: show only avatar icon without dropdown */}
                  {isGuide && isGuideProfile ? <Avatar user={user} size={32} /> : <UserMenu />}
                </Flex>
              </div>
            )}
          </Flex>
        </Content>
      </AntdHeader>
      {showGuideNavigation ? <GuideNavigation /> : null}
      {showTripFiltersNavigation ? <TripFiltersNavigation visible={isClientLandingPage} fluid={!isClientLandingPage} /> : null}
    </div>
  )
}

export default withDefaultErrorBoundary(GuestHeader)
